import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  FormSelectorBaseComponent,
  Instance,
  InstanceResolveData,
  LoadingService,
  ViewType,
} from '@finra/rampdi-forms-common';
import { OverlayService } from '@finra/forms';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

import { CompletenessCheckService } from '../../services/completeness-check.service';
import { InstanceService, MU3WithSupportingData } from '../../services/instance.service';
import { NavigationDataService } from '../../services/navigation-data.service';
import { RedlineService } from '../../services/redline.service';
import { SchemaService } from '../../services/schema.service';
import { DisplayRulesService, FilingStatusDisplayRules } from '../../services/display-rules.service';
import { GlobalContext } from '../../services/global-context.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-form-selector',
  templateUrl: './form-selector.component.html',
  styleUrls: ['./form-selector.component.scss'],
})
export class FormSelectorComponent
  extends FormSelectorBaseComponent<
    MU3WithSupportingData,
    Instance<any>,
    InstanceResolveData<MU3WithSupportingData, Instance<any>>
  >
  implements OnInit, OnDestroy
{
  private DRS = inject(DisplayRulesService);
  private GC = inject(GlobalContext);
  private isDestroying$ = new Subject<void>();

  displayRules$: Observable<FilingStatusDisplayRules> = this.DRS.displayRules$;

  hasReadOnlyMode$$ = toSignal(
    this.displayRules$.pipe(
      takeUntil(this.isDestroying$),
      map((res) => res && Object.keys(res)?.length > 0 && res['readOnlyMode']),
    ),
  );

  permissions$$ = toSignal(
    this.displayRules$.pipe(
      takeUntil(this.isDestroying$),
      map((res) => res),
    ),
  );

  showValidationOnCC$$ = toSignal(
    this.CC.completenessCheck$.pipe(
      takeUntil(this.isDestroying$),
      map((resp) => (resp?.['open'] ? resp['open'] : false)),
    ),
  );

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected location: Location,
    protected LS: LoadingService,
    protected SS: SchemaService,
    protected CC: CompletenessCheckService,
    protected RL: RedlineService,
    protected OS: OverlayService,
    protected NS: NavigationDataService,
    protected IS: InstanceService,
    protected DS: DisplayRulesService,
  ) {
    super(route, router, location, SS, CC, RL, OS, NS, IS);
  }

  ngOnInit(): void {
    this.formInfo$ = this.getFormInfo().pipe(
      map((formInfo) => {
        const params = this.route.snapshot.paramMap;
        const sectionId = params.get('sectionId');
        const licenseTypeCode = params.get('licenseTypeCode');
        const viewType = params.get('viewType') as ViewType;
        const navId = sectionId + (licenseTypeCode ? `;${licenseTypeCode}` : '');
        this.IS.viewType = viewType;
        this.sectionData = this.NDS.getNavItem(
          `${viewType}.${this.route.snapshot.data.instanceData?.instance?.instanceMetadata?.attributes?.filingTypeCode}`,
          navId,
        );
        return formInfo;
      }),
    );
  }

  setOptions(params: ParamMap, data: any, showRedlines) {
    const sectionId = params.get('sectionId');
    const viewType = params.get('viewType');

    const {
      instance: {
        instanceMetadata: md,
        collectedData: cd,
        id,
        templateName: tn,
        templateNamespace: tns,
        templateVersion: tv,
      },
      supportingData: sd,
    } = data.instanceData;

    const options = {
      ...data.user,
      context: {
        disabled: viewType === 'formView' || this.hasReadOnlyMode$$() ? true : undefined,
        isLegalView: viewType === 'formView',
      },
      gatewayOrigin: environment.gatewayOrigin,
      isCompanyUser: this.DS.userPermissions.isCompanyUser,
      templateName: tn,
      templateVersion: tv,
      namespace: tns,
      instanceId: data.instanceData?.instance.id,
      datacollectionUrl: environment.urls.datacollection,
      filingTypeCode: data.instanceData?.instance?.instanceMetadata?.attributes?.filingTypeCode,
      currentInstance: this.IS.currentInstance,
      originalInstance: this.IS.getInitialInstance(),
      permissions: this.permissions$$(),
      hasReadOnlyMode: this.hasReadOnlyMode$$(),
      showRedlines,
      showValidation: this.showValidationOnCC$$() ?? false,
      subjectId: this.IS.currentInstance?.instanceMetadata?.target?.['subjectId'],
      // --- formState functions required by individualInformation layout ---
      getLegalName: (model) =>
        `${model?.firstName ?? ''} ${model?.middleName ? model.middleName + ' ' : ''}${model?.lastName ?? ''}${model?.suffix ? ' ' + model.suffix : ''}`,
      checkNameChange: (fieldConfig) => {
        return (
          JSON.stringify(fieldConfig.model?.personalInformation?.individualName) !==
          JSON.stringify(
            this.IS.getInitialInstance()?.collectedData?.individualInformation?.personalInformation?.individualName,
          )
        );
      },
      // @mu3-todo(pending-section): No-op stubs — replace with real implementations when section features are built
      getLearnMoreLink: (_sectionName: string) => '',
      clearFieldValues: (_fc, ..._keys) => {},
      clearFromControl: (_fc, _key) => {},
      determineModalTitle: (_mode, _value) => '',
      editIndividualName: (_fc) => {},
      enableNotificationEmail: (_fc) => {},
      updateNotificationEmail: (_component, _event) => {},
      handleAddUserSuffix: (_fieldConfig, _value) => {},
      handleAddUserSuffixViewMode: (_fieldConfig, _value) => {},
      updateIDVStatus: (_context?) => {},
      canViewGovtId: false,
      canViewPassportNumber: false,
      editEmail: false,
    };

    this.IS.setdataforAll(options);
    return options;
  }

  onDataChange(data: any) {
    data = this.trimObjectValue(data);
    this.IS.addDataChangesToInstance(data, this.sectionData?.modelPath || [this.sectionData?.id]);
  }

  refreshFormData() {
    this.formInfo$ = this.getFormInfo().pipe(
      map((formInfo) => {
        const params = this.route.snapshot.paramMap;
        const sectionId = params.get('sectionId');
        const licenseTypeCode = params.get('licenseTypeCode');
        const viewType = params.get('viewType') as ViewType;
        const navId = sectionId + (licenseTypeCode ? `;${licenseTypeCode}` : '');
        this.IS.viewType = viewType;
        this.sectionData = this.NDS.getNavItem(
          `${viewType}.${this.route.snapshot.data.instanceData?.instance?.instanceMetadata?.attributes?.filingTypeCode}`,
          navId,
        );
        return formInfo;
      }),
    );
  }

  onStatusChange(status) {
    if (!this.hasReadOnlyMode$$()) {
      globalThis.finformUnsavedChanges = this.IS.hasUnsavedChanges([]);
    }
  }

  private trimObjectValue(obj: any, visited = new WeakSet()) {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }
    if (visited.has(obj)) {
      return obj;
    }
    visited.add(obj);
    const result: any = Array.isArray(obj) ? [] : {};
    for (const key in obj) {
      const value = obj[key];
      if (typeof value === 'string') {
        result[key] = value.trim();
      } else if (typeof value === 'object' && value !== null) {
        result[key] = this.trimObjectValue(value, visited);
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  ngOnDestroy(): void {
    this.isDestroying$.next(undefined);
    this.isDestroying$.complete();
  }
}
